import { Canceler } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PickerInput from './components/PickerInput/PickerInput';
import PickerOption from './components/PickerOption/PickerOption';
import PickerLazyListContent from './containers/PickerLazyListContent/PickerLazyListContent';
import PickerStaticListContent from './containers/PickerStaticListContent/PickerStaticListContent';
import { getPickersPosition, getUnavailableMessage, onOptionSelect, transformToPickerOption } from './helpers';
import { IPickerOption, IPickerProps, PickerPosition, PickerType } from './types';
import { defaultPagination } from 'src/constants/main';
import { dropdownMaxOptions } from 'src/constants/styles';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { IPagination } from 'src/types/main';
import { arrayMerge } from 'src/utils/formatter';
import { arrayCompare, getCancelToken } from 'src/utils/helpers';
import Portal from 'src/view/components/Portal/Portal';
import logger from '~/services/logger';
import './style.scss';

/**
 * @see PickerType
 * @see IPickerOption
 * @see PickerPosition
 * @see IPickerValidation
 * @see PICKER_UNAVAILABLE_TYPE
 *
 * Main props:
 * @param {TPickerType} type - static or lazy. Default is LAZY.
 * @param {Array<IPickerOption>} selectedOptions - arrays of selected options. Default an empty array.
 * @callback onSelect - callBack when choosing option or click reset.
 *
 * For LAZY type props:
 * @param {string} [fetchUrl] - url address for request. Default - none.
 * Important! Without it, there will be no request.
 * @param {object} [fetchParams] - parameters object for request. Default - none.
 * Here you can send: per_page, is_delete etc.
 * @param {boolean} [autoFetch] - whether to run a query when component mounts. Default - none.
 * Without it, the request will be made only when opening or scrolling.
 * @callback [returnFullData] - returns all data that was received during request.
 *
 * For STATIC type props:
 * @param {Array<IPickerOption>} [options] - list of options. Default an empty array.
 *
 * Configurations props:
 * @param {string} [placeholder] - placeholder in language key format. Default 'select_item'.
 * @param {string} [emptyPlaceHolder] - emptyPlaceHolder in language key format.'.
 * @param {string} [emptyText] - text if option list is empty. Default 'items_not_found for LAZY and 'options_not_found' for STATIC.
 * @param {boolean} [isMulti] - multiple choice of options. Default - none.
 * @param {boolean} [searchable] - presence or absence of a search string. Default - none.
 * @param {boolean} [hideResetIcon] - presence or absence of a reset button. Default - false.
 * @param {boolean} [hideSelectedIcons] - presence or absence of a check mark - marks of selected options.
 * Important! Disabling marks changes highlighting style of  selected options. Default - none.
 * @param {boolean} [noReset] - disabled reset, at least one option will remain selected. Default - none.
 * @param {boolean} [disabled] - disabled. Default - none.
 * @param {boolean} [loading] - loading visualization. Default - none.
 * @param {TPickerPosition} [position] - choice of positioning of options sheet relative to picker.
 * Default - ABSOLUTE.
 * FIXED positioning will help to bypass overflow = hidden.
 * PORTAL positioning will help in portal modals.
 * @param {IPickerValidation} [validation] - validation object. Default - none. See IPickerValidation type.
 *
 * Unavailable configurations props:
 * Important! Works only for products and categories.
 * Important! To work, you need to enable "markUnavailable" is available and pass "unavailableType".
 * @param {boolean} [markUnavailable] - mark unavailable options. Default - none.
 * @param {PICKER_UNAVAILABLE_TYPE} [unavailableType] - option type to check availability. Default - none.
 * @param {boolean} [disableUnavailable] - prohibition of selection of unavailable options. Default - none.
 *
 * Styles and classNames props:
 * @param {string} [className] - main wrapper className. Default - empty string.
 * @param {string} [inputClassName] - Default - none.
 * @param {string} [defaultPlaceholderClassName] - className for default placeholder (before selecting options). Default - none.
 * @param {string} [optionClassName] - Default - none.
 * @param {string} [listClassName] - options list className. Default - none.
 * @param {string} [emptyListTextClassName] - Default - none.
 * @param {number} [numberVisibleOptionsInDropdown] - number of options that can be seen at same time when picker is opened.
 * Default - 8.
 * Important! 0.5 is always added to this number for more evidence of scrolling.
 * @param {number} [maxListWidth] - limiting maximum width(in pixels) of list of options. Default - none.
 * @param {'top' | 'bottom'} [listPlacement] - determines where to show the list of options. Default - 'bottom'.
 * @param {'icon' | 'input'} [viewedType] - input display style. Default - 'input'.
 * Explanation: display a simulated input field with a placeholder or icon.
 * @param {'small' | 'normal'} [viewedSize] - picker display preset - 'small' or 'normal'. Default - 'normal.
 *
 * Other props:
 * @callback [onTogglePicker] - called during interaction with picker (opening and closed).
 * Returns boolean value of picker's open state.
 */
const Picker = <T,>({
  // main
  type = PickerType.STATIC,
  selectedOptions = [],
  // for static type
  options: propsOptions = [],
  // configurations
  placeholder = localeStore.t('select_item'),
  emptyPlaceHolder,
  hideResetIcon = false,
  position = PickerPosition.ABSOLUTE,
  // styles and classNames
  className = '',
  numberVisibleOptionsInDropdown = dropdownMaxOptions,
  listPlacement = 'bottom',
  viewedType = 'input',
  viewedSize = 'normal',
  ...props
}: IPickerProps<T>): JSX.Element => {
  // canceller
  let cancel: Canceler | null = null;
  // for check first render
  const isFirstRender: React.MutableRefObject<boolean> = useRef(false);
  // for list style
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  const [listRef, setListRef] = useState<HTMLUListElement | null>(null);
  // for lazy picker
  const [pagination, setPagination] = useState<IPagination<T>>(defaultPagination);
  const [pages, setPages] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  // for static picker
  const [options, setOptions] = useState<IPickerOption[]>(propsOptions);
  //
  const [searchValue, setSearchValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (props.autoFetch) onFetch(1);
  }, []);

  useEffect(() => {
    if (type === PickerType.STATIC && !arrayCompare(propsOptions, options, true) && !searchValue) setOptions(propsOptions);
  }, [propsOptions]);

  useEffect(() => {
    // return, if this is first render
    if (!isFirstRender.current) {
      isFirstRender.current = true;
      return;
    }

    searchHandler();
  }, [searchValue]);

  const onFetch = async (page: number = pagination.current_page + 1, clear?: boolean) => {
    try {
      if (!props.fetchUrl || props.disabled || props.loading) return;
      if (cancel) cancel('Cancelled');

      const params = { page, name: searchValue.toLowerCase().trim(), is_delete: 0 };
      setLoading(true);

      const _pagination: IPagination<T> = await client.get(props.fetchUrl, {
        params: props.fetchParams ? { ...props.fetchParams, ...params } : params,
        cancelToken: getCancelToken((c) => (cancel = c)),
      });

      let data: T[] = [];
      let _pages: number[] = pages;

      if (
        (_pagination.current_page === 1 && !pages.length) ||
        (_pagination.current_page <= 1 && !_pagination.data.length) ||
        !pagination.current_page ||
        clear
      ) {
        data = _pagination.data;
        _pages = [];
      } else if (pagination.current_page > page) {
        data = arrayMerge(_pagination.data, pagination.data, 'id');
      } else {
        data = arrayMerge(pagination.data, _pagination.data, 'id');
      }
      _pages = [...pages, _pagination.current_page].sort((a, b) => (a > b ? 1 : -1));

      if (props.returnFullData) props.returnFullData(data);
      setPagination({ ..._pagination, data });
      setLoading(false);
      setPages(_pages);
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      setLoading(false);
      logger.error({ error, place: 'Picker onFetch' });
      return [];
    }
  };

  const toggleOpen = useCallback(
    (close?: boolean) => {
      if (props.disabled || props.loading) return;

      const newClose: boolean = close ? false : !isOpen;
      // call onTogglePicker input if it is available
      if (props.onTogglePicker) props.onTogglePicker(newClose);
      // open dropdown
      setIsOpen(newClose);
    },
    [props.disabled, props.loading, props.onTogglePicker, isOpen]
  );

  const onSearch = (search: string) => {
    setSearchValue(search);
  };
  const searchHandler = () => {
    if (type === PickerType.LAZY) {
      if (!searchValue.length || searchValue.length >= 2) onFetch(1, true);
      return;
    }

    setOptions(propsOptions.filter((option) => option.label.toLowerCase().trim().includes(searchValue.toLowerCase().trim())));
  };

  const onSelect = useCallback(
    (option: IPickerOption) => {
      onOptionSelect({
        isMulti: props.isMulti,
        noReset: props.noReset,
        option,
        selectedOptions,
        toggleOpen,
        onSelect: props.onSelect,
      });
    },
    [props.isMulti, props.noReset, selectedOptions, props.onSelect, toggleOpen]
  );

  const clickReset = useCallback(() => {
    if (props.disabled || props.loading || (props.noReset && !selectedOptions.length)) return;

    props.onSelect(props.noReset ? [selectedOptions[0]] : []);
    if (isOpen && !props.isMulti) toggleOpen();
  }, [props.disabled, props.loading, props.noReset, selectedOptions, props.onSelect, props.isMulti, isOpen, toggleOpen]);

  const isLazy = useMemo((): boolean => {
    return type === PickerType.LAZY;
  }, [type]);

  const showSearch = useMemo(
    () => Boolean(props.searchable && (options.length || searchValue || pagination.data.length)),
    [props.searchable, options.length, searchValue, pagination.data.length]
  );

  const listStyle = useMemo(() => {
    return getPickersPosition({
      position,
      wrapperRef,
      listRef,
      maxListWidth: props.maxListWidth,
      //
      listPlacement,
      listLength: isLazy ? pagination.data.length : options.length,
      isSearchable: showSearch,
      numberVisibleOptionsInDropdown,
      smallOptions: viewedSize === 'small',
    });
  }, [
    position,
    wrapperRef,
    listRef,
    props.maxListWidth,
    listPlacement,
    isLazy,
    pagination.data,
    options,
    showSearch,
    numberVisibleOptionsInDropdown,
    viewedSize,
  ]);

  const wrapperClassName = useMemo((): string => {
    let _className: string = 'custom-picker';

    if (className) _className += ` ${className}`;
    if (viewedSize === 'small') _className += ' custom-picker_small';
    if (props.disabled) {
      _className += ' custom-picker_disabled';
    } else if (loading || props.loading) {
      _className += ` custom-picker_loading`;
    }

    return _className;
  }, [className, props.disabled, loading, props.loading, viewedSize]);

  const renderItem = useCallback(
    (item: T | IPickerOption | undefined, index: number): JSX.Element | null => {
      if (!item) return null;

      let _item: IPickerOption = isLazy ? transformToPickerOption(item) : (item as IPickerOption);
      const unavailable: string | null = props.markUnavailable ? getUnavailableMessage({ item, unavailableType: props.unavailableType }) : null;
      const disabled: boolean = Boolean(props.disableUnavailable && unavailable);

      // add translation on labels if its not lazy list
      if (!isLazy) {
        _item = {
          label: localeStore.t(_item.label),
          value: _item.value,
          warringText: _item.warringText,
          disabled: !!_item?.disabled,
          data: _item.data,
        };
      }

      return (
        <PickerOption
          key={index}
          item={_item}
          isSelected={selectedOptions.some((option) => option.value === _item.value)}
          unavailable={props.markUnavailable ? getUnavailableMessage({ item, unavailableType: props.unavailableType }) : _item?.warringText ?? null}
          disabled={disabled || _item.disabled}
          viewedSize={viewedSize}
          onClick={onSelect}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      );
    },
    [selectedOptions, isLazy, viewedSize, onSelect, props]
  );

  const renderList = (): JSX.Element => {
    return (
      <ul ref={(ref) => setListRef(ref)} className={`custom-picker__wrapper full-height ${props.listClassName || ''}`} style={listStyle}>
        {isLazy ? (
          <PickerLazyListContent<T>
            searchValue={searchValue}
            showSearch={showSearch}
            loading={loading}
            viewedSize={viewedSize}
            pages={pages}
            pagination={pagination}
            onFetch={onFetch}
            renderItem={renderItem}
            onSearch={onSearch}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        ) : (
          <PickerStaticListContent<T>
            options={options}
            searchValue={searchValue}
            showSearch={showSearch}
            renderItem={renderItem}
            onSearch={onSearch}
            viewedSize={viewedSize}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          />
        )}
      </ul>
    );
  };

  return (
    <div ref={(ref) => setWrapperRef(ref)} className={wrapperClassName}>
      <PickerInput
        isOpen={isOpen}
        loading={loading || props.loading}
        options={options}
        propsOptions={propsOptions}
        selectedOptions={selectedOptions}
        hideResetIcon={hideResetIcon}
        placeholder={placeholder}
        emptyPlaceHolder={emptyPlaceHolder}
        viewedType={viewedType}
        viewedSize={viewedSize}
        toggleOpen={() => toggleOpen()}
        clickReset={clickReset}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
      {isOpen ? (
        <>
          {position === PickerPosition.PORTAL ? <Portal>{renderList()}</Portal> : renderList()}
          <div className="custom-picker__overlay" onClick={() => toggleOpen(true)} />
        </>
      ) : null}
      {props.validation && !props.validation.isValid && props.validation.errorText ? (
        <span className={`custom-picker__validation-block ${props.validation.errorBlockClassName || ''}`}>{props.validation.errorText}</span>
      ) : null}
    </div>
  );
};

export default Picker;
